// ETA wording (spec 003 FR-023), the same as the apps' shared Eta, from
// i18n/common (spec 013 FR-010).
import type { Destination, ETA, MemberLocation } from '../api/types';
import { integer, t } from '../i18n/index.ts';

// shownDestination is the destination whose ETA a member's pin shows: the
// viewer's own for them first, else the member's heading (spec 003
// 003 FR-023).
export function shownDestination(m: MemberLocation): Destination | undefined {
  if (m.destination?.eta) return m.destination;
  if (m.heading?.eta) return m.heading;
  return m.destination ?? m.heading;
}

// secondsLeft is the time left: counted down to the arrival time while the
// member moves, else the travel time as if they left now.
export function secondsLeft(eta: ETA, now: number): number {
  if (!eta.moving) return eta.seconds;
  return Math.max(0, (Date.parse(eta.arrive_at) - now) / 1000);
}

// hm writes a duration as hours and minutes, 1:32 or 0:18.
export function hm(seconds: number): string {
  const mins = Math.round(seconds / 60);
  return t('durationClock', { hours: Math.floor(mins / 60), minutes: String(mins % 60).padStart(2, '0') });
}

// movingFor is how old a moving member's fix may be and still count down: a
// moving phone sends every minute. staleAfter matches the server's
// location.StaleAfter.
const movingFor = 5 * 60_000;
const staleAfter = 75 * 60_000;

// pinText is all a pin says of an ETA: the speed while moving and the time
// left, as 45 MPH ETA 1:32 (spec 003 FR-023), or '' without one. An old
// fix keeps its age on the pin instead, so the ETA never counts down from a
// position that is no longer current (constitution III): a moving fix past
// 5 minutes, or a still one past 75 unless the phone still reports.
export function pinText(m: MemberLocation, now: number, miles: boolean): string {
  const eta = shownDestination(m)?.eta;
  if (!eta) return '';
  const age = now - Date.parse(eta.as_of);
  if (eta.moving ? age > movingFor : m.status !== 'current' && age > staleAfter) return '';
  const left = hm(secondsLeft(eta, now));
  const mps = m.location?.speed_mps;
  if (!eta.moving || mps == null || mps < 1) return t('etaPin', { left });
  return miles
    ? t('etaPinMph', { speed: integer(Math.round(mps * 2.23694)), left })
    : t('etaPinKmh', { speed: integer(Math.round(mps * 3.6)), left });
}

// minutes writes a duration in words for the sheet: 18 min, 1 h, 1 h 32 min.
export function minutes(seconds: number): string {
  const mins = Math.max(1, Math.round(seconds / 60));
  if (mins < 60) return t('durationMinutes', { minutes: mins });
  if (mins % 60 === 0) return t('durationHours', { hours: mins / 60 });
  return t('durationHoursMinutes', { hours: Math.floor(mins / 60), minutes: mins % 60 });
}
